import { ExtendedTasksResponse } from 'app/console-api/tasks/tasks-extended';
import { StyledCaption } from 'app/components/management/apps/create/apps-create-step-1';
import { BodyText, Button, FlexColumn, FlexRow, formatDate } from '@make-software/cspr-design';
import React from 'react';
import DetailsNameTableData from 'app/components/management/common/details-name-table-data/details-name-table-data';
import DetailsValueTableData from 'app/components/management/common/details-value-table-data/details-value-table-data';
import DetailsTableRow from 'app/components/management/common/details-table-row/details-table-row';
import { GetPluginMetaByID } from 'app/components/management/common/plugins/plugin-metadata';
import CollapsibleContentPreview from 'app/components/management/common/content-view/collapse-content-preview';
import { RewardsViewBlock } from 'app/components/management/common/rewards/rewards-view';
import { EditButton, StyledChipsTag, StyledLink } from 'app/components/common/common-styles';
import { RewardsType } from 'app/console-api/apps/types';
import { generatePath } from 'react-router-dom';
import { APPS_TASKS_VIEW_LOGS_PATH } from 'app/route/paths';
import { TasksStatusChips } from 'app/components/common/tasks-statuses/tasks-status-chips';
import { pluginFormControlMap } from 'app/components/management/common/plugins/form-control-map';
import { PluginChip } from 'app/components/management/common/plugins/plugin-chip/plugin-chip';

interface TaskViewPageContentProps {
  rewardsTypes: RewardsType[];
  data: ExtendedTasksResponse;
  onEdit: (data: ExtendedTasksResponse) => void;
}

export const TaskViewPageContent = ({ rewardsTypes, data, onEdit }: TaskViewPageContentProps) => {
  const pluginRegistry = pluginFormControlMap[data.plugin.id];

  return (
    <FlexColumn>
      <StyledCaption margin={'24px 0 32px 17px'}>
        <BodyText size={1} scale={'lg'}>
          Details
        </BodyText>
      </StyledCaption>
      <DetailsTableRow>
        <DetailsNameTableData>ID</DetailsNameTableData>
        <DetailsValueTableData>{data.task.id}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Type</DetailsNameTableData>
        <DetailsValueTableData>
          <FlexRow align={'center'} gap={4}>
            <PluginChip id={data.task.pluginID}></PluginChip>
          </FlexRow>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Status</DetailsNameTableData>
        <DetailsValueTableData>
          <TasksStatusChips status={data.task.status} isActive={data.task.isActive} />
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>App</DetailsNameTableData>
        <DetailsValueTableData>{data.app.name}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Name</DetailsNameTableData>
        <DetailsValueTableData>{data.task.name}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Description</DetailsNameTableData>
        <DetailsValueTableData>{data.task.description}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Rewards</DetailsNameTableData>
        <DetailsValueTableData>
          <RewardsViewBlock
            rewardValues={data.rewards}
            rewardTypes={rewardsTypes}
          ></RewardsViewBlock>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Categories</DetailsNameTableData>
        <DetailsValueTableData>
          <FlexRow wrap={'wrap'} gap={8}>
            {data.categories.map((cat, idx) => (
              <StyledChipsTag key={idx}>{cat}</StyledChipsTag>
            ))}
          </FlexRow>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Custom metadata</DetailsNameTableData>
        <DetailsValueTableData>
          <CollapsibleContentPreview json={data.task.customMetadata} />
        </DetailsValueTableData>
      </DetailsTableRow>
      {data.task.pluginMetadata && (
        <DetailsTableRow>
          <DetailsNameTableData>Plugin metadata</DetailsNameTableData>
          <DetailsValueTableData>
            <CollapsibleContentPreview json={data.task.pluginMetadata} />
          </DetailsValueTableData>
        </DetailsTableRow>
      )}
      {pluginRegistry?.viewSectionComponent && (
        <pluginRegistry.viewSectionComponent metadata={data.task.pluginMetadata} />
      )}
      <DetailsTableRow>
        <DetailsNameTableData>Created At</DetailsNameTableData>
        <DetailsValueTableData>
          {formatDate(data.task.createdAt.toLocaleString())}
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Updated At</DetailsNameTableData>
        <DetailsValueTableData>
          {formatDate(data.task.updatedAt.toLocaleString())}
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>
          <StyledLink
            to={generatePath(APPS_TASKS_VIEW_LOGS_PATH, {
              appID: data.app.id,
              id: data.task.id
            })}
          >
            <BodyText size={3}>See all action logs</BodyText>
          </StyledLink>
        </DetailsNameTableData>
      </DetailsTableRow>
      <EditButton onClick={onEdit}>Edit</EditButton>
    </FlexColumn>
  );
};
